import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Sparkles,
  MessageCircle,
  Star,
  Send,
  Zap,
  UserPlus,
  X,
} from 'lucide-react';
import {
  ApiException,
  ChatCompanionRequestDto,
  ChatCompanionResponseDto,
  ChatRequestDto,
  UserNatalChartListItemDto,
} from '../api';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { formatCivilBirthDate } from '../helpers/birthDateTime';
import './ChatAI.css';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useAuth } from 'react-oidc-context';
import { HttpError } from '../http';
import { appStorage } from '../platform/runtime';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

type StoredMessage = Omit<Message, 'timestamp'> & { timestamp: string };

const CHAT_STORAGE_KEY = 'chatMessages';
const CHAT_MAX_QUESTION_LENGTH = 500;

type ProblemDetailsLike = {
  detail?: string;
  title?: string;
  errors?: Record<string, string[]>;
};

const ChatAI: React.FC = () => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const userId = auth.user?.profile.sub;

  const storageKey = useMemo(
    () => (userId ? `${CHAT_STORAGE_KEY}:${userId}` : 'chatMessages:anon'),
    [userId]
  );

  const createWelcomeMessage = (): Message => ({
    id: '1',
    role: 'assistant',
    content: t('chatWelcomeMessage'),
    timestamp: new Date()
  });

  const getInitialMessages = (): Message[] => {
    const stored = appStorage.session.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as StoredMessage[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map(message => ({
            ...message,
            timestamp: new Date(message.timestamp)
          }));
        }
      } catch (error) {
        console.error('Failed to parse stored chat messages', error);
      }
    }

    return [createWelcomeMessage()];
  };

  const [messages, setMessages] = useState<Message[]>(getInitialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeCompanion, setActiveCompanion] = useState<ChatCompanionResponseDto | null>(null);
  const [availableCharts, setAvailableCharts] = useState<UserNatalChartListItemDto[]>([]);
  const [isCompanionPickerOpen, setIsCompanionPickerOpen] = useState(false);
  const [isCompanionLoading, setIsCompanionLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const api = useAstroApiClient();

  useEffect(() => {
    let cancelled = false;

    const loadCompanion = async () => {
      try {
        const companion = await api.apiChatCompanionGet();
        if (!cancelled) {
          setActiveCompanion(companion ?? null);
        }
      } catch {
        if (!cancelled) {
          setActiveCompanion(null);
        }
      }
    };

    loadCompanion();

    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isLoading]);

  useEffect(() => {
    const serialized: StoredMessage[] = messages.map(message => ({
      ...message,
      timestamp: message.timestamp.toISOString()
    }));

    appStorage.session.setItem(storageKey, JSON.stringify(serialized));
  }, [messages, storageKey]);

  const quickQuestionKeys = [
    'chatQuickQuestionTransitsToday',
    'chatQuickQuestionProjectDate2026',
    'chatQuickQuestionMercuryRetrogradeEnd',
  ] as const;
  const helpTopicKeys = [
    'chatHelpWithNatalChart',
    'chatHelpWithTransitsForecasts',
    'chatHelpWithAstrologicalTiming',
    'chatHelpWithAspectMeaning',
    'chatHelpWithSpiritualGuidance',
    'chatHelpWithHouseAnalysis',
    'chatHelpWithPlanetaryReturns',
  ] as const;

  const getProblemDetailsMessage = (problem?: ProblemDetailsLike): string | null => {
    if (!problem) {
      return null;
    }

    const validationMessage = problem.errors
      ? Object.values(problem.errors)
          .flat()
          .find((message): message is string => typeof message === 'string' && message.trim().length > 0)
      : undefined;

    const message = problem.detail ?? validationMessage ?? problem.title;
    return message && message.trim().length > 0 ? message : null;
  };

  const parseProblemDetails = (response: string): ProblemDetailsLike | null => {
    try {
      return JSON.parse(response) as ProblemDetailsLike;
    } catch {
      return null;
    }
  };

  const getErrorMessage = (error: unknown): string => {
    if (ApiException.isApiException(error)) {
      const apiMessage = getProblemDetailsMessage(error.result as ProblemDetailsLike | undefined);
      if (apiMessage) {
        return apiMessage;
      }

      if (error.response) {
        const parsed = parseProblemDetails(error.response);
        const responseMessage = getProblemDetailsMessage(parsed ?? undefined);
        if (responseMessage) {
          return responseMessage;
        }
      }

      return t('chatErrorAstros');
    }

    if (error instanceof HttpError) {
      return t('chatErrorAstros');
    }

    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }

    return t('chatErrorAstros');
  };

  const openCompanionPicker = async () => {
    setIsCompanionPickerOpen(true);
    setIsCompanionLoading(true);

    try {
      const response = await api.apiAstrologyNatalChartsGet();
      setAvailableCharts((response.charts ?? []).filter(chart => !chart.isPrimary));
    } catch {
      setAvailableCharts([]);
    } finally {
      setIsCompanionLoading(false);
    }
  };

  const setCompanion = async (chart: UserNatalChartListItemDto) => {
    if (!chart.id) {
      return;
    }

    await api.apiChatCompanionPost(new ChatCompanionRequestDto({ natalChartId: chart.id }));
    setActiveCompanion(new ChatCompanionResponseDto({
      natalChartId: chart.id,
      name: chart.name ?? '',
      birthDateTime: chart.birthDateTime,
      birthLocalDate: chart.birthLocalDate,
      birthLocalTime: chart.birthLocalTime,
      timeZone: chart.timeZone ?? 'UTC',
    }));
    setIsCompanionPickerOpen(false);
  };

  const clearCompanion = async () => {
    await api.apiChatCompanionDelete();
    setActiveCompanion(null);
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const content = input.trim();

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    const assistantId = (Date.now() + 1).toString();

    try {
      const res = await api.apiChat(new ChatRequestDto({ question: content }));
      if (res.activeCompanion !== undefined) {
        setActiveCompanion(res.activeCompanion ?? null);
      }

      let messageContent = res.answer || '';
      if (res.disclaimer) {
        messageContent += `\n\n${res.disclaimer}`;
      }
      const aiMessage: Message = {
        id: assistantId,
        role: 'assistant',
        content: messageContent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      const aiMessage: Message = {
        id: assistantId,
        role: 'assistant',
        content: getErrorMessage(err),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuestion = (question: string) => {
    setInput(question);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">{t('chatTitle')}</h2>
          <p className="text-slate-600 mt-1">{t('chatDescription')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <Card className="bg-white border-stone-200 shadow-sm h-[600px] flex flex-col">
            <CardHeader className="border-b border-stone-200">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <CardTitle className="text-slate-800 flex items-center">
                  <MessageCircle className="h-5 w-5 mr-2 text-action-500" />
                  {t('astrologicalConversation')}
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  {activeCompanion && (
                    <span className="inline-flex items-center gap-2 rounded-full border border-action-200 bg-action-50 px-3 py-1 text-xs font-medium text-slate-700">
                      {t('chatCompanionActive', { name: activeCompanion.name })}
                      <button
                        type="button"
                        onClick={clearCompanion}
                        className="rounded-full p-0.5 text-slate-500 hover:bg-action-100 hover:text-slate-800"
                        aria-label={t('chatCompanionRemove')}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openCompanionPicker}
                    aria-label={t('chatCompanionAdd')}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('chatCompanionAdd')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col min-h-0 p-0">
              <ScrollArea className="flex-1 min-h-0 p-4">
                <div className="space-y-4">
                  {messages.map(message => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${message.role === "user"
                          ? "bg-action-700 text-white"
                          : "bg-stone-50 text-slate-700 border border-stone-200"
                          }`}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-center mb-2">
                            <Star className="h-4 w-4 text-yellow-400 mr-2" />
                            <span className="text-xs text-slate-500 font-medium">{t('chatAssistantLabel')}</span>
                          </div>
                        )}
                        <div className="whitespace-pre-line text-sm"
                          dangerouslySetInnerHTML={{
                            __html: message.content.replace(/\n/g, '<br/>')
                          }}
                        />
                        <div className="text-xs opacity-70 mt-2">{message.timestamp.toLocaleTimeString()}</div>
                      </div>
                    </div>
                  ))}

                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-stone-50 text-slate-700 border border-stone-200 p-3 rounded-lg">
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400 mr-2" />
                          <span className="text-xs text-slate-500 font-medium">{t('chatAssistantLabel')}</span>
                        </div>
                        <div className="flex items-center mt-2">
                          <div className="animate-pulse flex space-x-1">
                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                          </div>
                          <span className="ml-2 text-sm text-slate-600">{t('chatConsultingAstros')}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              <div className="p-2 border-t border-stone-200">
                <div className="flex space-x-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    maxLength={CHAT_MAX_QUESTION_LENGTH}
                    placeholder={t('chatInputPlaceholder')}
                    className="flex-1 bg-white border-stone-300 text-slate-800 placeholder-slate-400"
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  />
                  <Button
                    onClick={handleSend}
                    disabled={isLoading || !input.trim()}
                    className="bg-action-700 hover:bg-action-800"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="bg-white border-stone-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 text-sm flex items-center">
                <Zap className="h-4 w-4 mr-2 text-yellow-400" />
                {t('chatQuickQuestionsTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {quickQuestionKeys.map((questionKey, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="sm"
                  className="w-full text-left justify-start text-slate-600 hover:text-slate-800 hover:bg-stone-100 h-auto p-2"
                  onClick={() => handleQuickQuestion(t(questionKey))}
                >
                  <span className="text-xs">{t(questionKey)}</span>
                </Button>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 text-sm flex items-center">
                <Sparkles className="h-4 w-4 mr-2 text-action-500" />
                {t('chatHelpWithTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-xs text-slate-600">
                {helpTopicKeys.map((topicKey) => (
                  <div key={topicKey}>- {t(topicKey)}</div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={isCompanionPickerOpen} onOpenChange={setIsCompanionPickerOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{t('chatCompanionPickerTitle')}</DialogTitle>
            <DialogDescription>{t('chatCompanionPickerDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {isCompanionLoading && (
              <p className="text-sm text-slate-600">{t('loading')}</p>
            )}
            {!isCompanionLoading && availableCharts.length === 0 && (
              <p className="text-sm text-slate-600">{t('chatCompanionEmpty')}</p>
            )}
            {!isCompanionLoading && availableCharts.map(chart => (
              <Button
                key={chart.id}
                type="button"
                variant="ghost"
                className="w-full justify-start"
                onClick={() => setCompanion(chart)}
              >
                <span className="flex flex-col items-start">
                  <span>{chart.name}</span>
                  {chart.birthLocalDate && (
                    <span className="text-xs text-slate-500">
                      {formatCivilBirthDate(chart.birthLocalDate, i18n.language)}
                    </span>
                  )}
                </span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatAI;

